import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, BarChart3, Eye, Lock, X, ShieldCheck } from "lucide-react";

const STATUS_COLOR = {
  draft:     "bg-slate-100 text-slate-600 border-slate-300",
  scheduled: "bg-blue-100 text-blue-700 border-blue-300",
  live:      "bg-emerald-100 text-emerald-700 border-emerald-300",
  closed:    "bg-slate-100 text-slate-500 border-slate-300",
  cancelled: "bg-rose-100 text-rose-700 border-rose-300",
  deleted:   "bg-slate-100 text-slate-400 border-slate-200",
};

function nowIsoLocal(plusMinutes = 0) {
  const d = new Date();
  d.setMinutes(d.getMinutes() + plusMinutes - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function fmtDt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return iso; }
}

export default function ElectionsManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {} | existing
  const [catalog, setCatalog] = useState({ types: [], eligibility: [] });
  const [resultsFor, setResultsFor] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/elections");
      setRows(r.data.elections || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load elections");
    }
  };

  useEffect(() => {
    load();
    api.get("/elections/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const del = async (eid) => {
    if (!window.confirm("Remove this election from the live list? Cast votes remain in the database.")) return;
    try {
      await api.delete(`/admin/elections/${eid}`);
      toast.success("Election removed");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not remove"); }
  };
  const publish = async (eid) => {
    try {
      await api.post(`/admin/elections/${eid}/publish-results`);
      toast.success("Results published");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not publish"); }
  };

  return (
    <div className="bg-white border border-navy/10 rounded-sm" data-testid="admin-elections">
      <div className="px-4 py-3 border-b border-navy/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-medium text-navy">Elections & polls · {rows.length}</h3>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <ShieldCheck size={11} className="text-emerald-700" />
            Votes are stored anonymously — voter identity is one-way hashed and never linked to choices.
          </p>
        </div>
        <button
          onClick={() => setEditing({})}
          data-testid="admin-election-new"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-navy text-pearl text-sm rounded-sm hover:bg-navy-light"
        >
          <Plus size={14} /> New election
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-center text-slate-500 py-10">No elections yet. Create your first one.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-pearl">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Eligibility</th>
                <th className="px-4 py-3">Window</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Votes</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy/5">
              {rows.map((e) => (
                <tr key={e.election_id} data-testid={`admin-election-row-${e.election_id}`}>
                  <td className="px-4 py-3 max-w-[280px]">
                    <div className="font-medium text-navy line-clamp-1">{e.title}</div>
                    <div className="text-xs text-slate-500 line-clamp-1">{e.question}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{e.election_type}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{e.eligibility}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {fmtDt(e.start_at)}<br />→ {fmtDt(e.end_at)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-sm ${STATUS_COLOR[e.status]}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono">{e.total_votes}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button onClick={() => setResultsFor(e)} className="text-xs px-2 py-1 bg-white border border-navy/15 hover:border-saffron rounded-sm" title="View results">
                        <BarChart3 size={13} />
                      </button>
                      <button onClick={() => setEditing(e)} className="text-xs px-2 py-1 bg-white border border-navy/15 hover:border-saffron rounded-sm" title="Edit">
                        <Edit3 size={13} />
                      </button>
                      {e.publish_results === "manual" && (
                        <button onClick={() => publish(e.election_id)} className="text-xs px-2 py-1 bg-saffron text-navy rounded-sm" title="Publish results">
                          <Eye size={13} />
                        </button>
                      )}
                      <button onClick={() => del(e.election_id)} className="text-xs px-2 py-1 bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-sm" title="Remove">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ElectionEditor
          existing={editing.election_id ? editing : null}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {resultsFor && (
        <ResultsModal election={resultsFor} onClose={() => setResultsFor(null)} />
      )}
    </div>
  );
}

function ElectionEditor({ existing, catalog, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(() => {
    if (existing) {
      return {
        title: existing.title || "",
        description: existing.description || "",
        election_type: existing.election_type || "poll",
        question: existing.question || "",
        eligibility: existing.eligibility || "all_members",
        start_at: existing.start_at ? existing.start_at.slice(0, 16) : nowIsoLocal(5),
        end_at: existing.end_at ? existing.end_at.slice(0, 16) : nowIsoLocal(60 * 24 * 7),
        allow_multiple: existing.allow_multiple || false,
        max_choices: existing.max_choices || 1,
        publish_results: existing.publish_results || "after_close",
        options: (existing.options || []).map((o) => ({
          option_id: o.option_id,
          label: o.label,
          description: o.description || "",
        })),
      };
    }
    return {
      title: "", description: "", election_type: "poll", question: "",
      eligibility: "all_members",
      start_at: nowIsoLocal(5),
      end_at: nowIsoLocal(60 * 24 * 7),
      allow_multiple: false, max_choices: 1,
      publish_results: "after_close",
      options: [{ label: "Yes" }, { label: "No" }, { label: "Abstain" }],
    };
  });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updOpt = (i, k, v) => setForm((f) => ({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o) }));
  const addOpt = () => setForm((f) => ({ ...f, options: [...f.options, { label: "" }] }));
  const rmOpt = (i) => setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        options: form.options.filter((o) => (o.label || "").trim()),
      };
      if (payload.options.length < 2) {
        toast.error("Please add at least two options");
        setBusy(false);
        return;
      }
      if (isEdit) {
        await api.put(`/admin/elections/${existing.election_id}`, payload);
        toast.success("Election updated");
      } else {
        await api.post("/admin/elections", payload);
        toast.success("Election created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-election-editor">
      <form onSubmit={submit} className="bg-pearl rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
          <div className="font-display text-lg text-navy">{isEdit ? "Edit election" : "New election"}</div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <Field label="Title">
            <input value={form.title} onChange={(e) => upd("title", e.target.value)} required maxLength={200} className="form-input w-full" data-testid="election-title" autoFocus />
          </Field>
          <Field label="Type">
            <select value={form.election_type} onChange={(e) => upd("election_type", e.target.value)} className="form-input w-full">
              {catalog.types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Question (shown above the ballot)">
            <input value={form.question} onChange={(e) => upd("question", e.target.value)} required maxLength={400} className="form-input w-full" data-testid="election-question" />
          </Field>
          <Field label="Description (optional context)">
            <textarea value={form.description} onChange={(e) => upd("description", e.target.value)} maxLength={4000} rows={3} className="form-input w-full font-serif" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Eligibility">
              <select value={form.eligibility} onChange={(e) => upd("eligibility", e.target.value)} className="form-input w-full" data-testid="election-eligibility">
                {catalog.eligibility.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
              </select>
            </Field>
            <Field label="When to publish results">
              <select value={form.publish_results} onChange={(e) => upd("publish_results", e.target.value)} className="form-input w-full">
                <option value="after_close">Automatically when voting closes</option>
                <option value="live">Live, while voting is open</option>
                <option value="manual">Manually — I'll publish later</option>
              </select>
            </Field>
            <Field label="Voting opens">
              <input type="datetime-local" value={form.start_at} onChange={(e) => upd("start_at", e.target.value)} required className="form-input w-full" />
            </Field>
            <Field label="Voting closes">
              <input type="datetime-local" value={form.end_at} onChange={(e) => upd("end_at", e.target.value)} required className="form-input w-full" />
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <label className="flex items-center gap-2 text-sm text-navy/85">
              <input type="checkbox" checked={form.allow_multiple} onChange={(e) => upd("allow_multiple", e.target.checked)} />
              Allow multiple choices
            </label>
            {form.allow_multiple && (
              <Field label="Max choices per voter">
                <input type="number" min={1} max={20} value={form.max_choices} onChange={(e) => upd("max_choices", parseInt(e.target.value || "1", 10))} className="form-input w-full" />
              </Field>
            )}
          </div>

          <div>
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Options / candidates</span>
            <div className="space-y-2">
              {form.options.map((o, i) => (
                <div key={i} className="bg-white border border-navy/10 rounded-md p-3" data-testid={`election-option-edit-${i}`}>
                  <div className="flex gap-2">
                    <input value={o.label} onChange={(e) => updOpt(i, "label", e.target.value)} placeholder={`Option ${i + 1}`} maxLength={140} className="form-input flex-1" />
                    <button type="button" onClick={() => rmOpt(i)} className="px-2 py-1 text-red-700 hover:bg-red-50 rounded" title="Remove">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <textarea value={o.description || ""} onChange={(e) => updOpt(i, "description", e.target.value)} placeholder="Description / manifesto (optional)" maxLength={2000} rows={2} className="form-input w-full mt-1.5 text-xs" />
                </div>
              ))}
            </div>
            <button type="button" onClick={addOpt} className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-navy/15 hover:border-saffron text-xs rounded-md">
              <Plus size={12} /> Add option
            </button>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-navy/10">
          <button type="button" onClick={onClose} className="px-4 py-2 text-navy/60 hover:text-navy text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="px-5 py-2 bg-navy text-pearl rounded-md text-sm disabled:opacity-40" data-testid="election-save">
            {busy ? "Saving…" : (isEdit ? "Update" : "Create")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ResultsModal({ election, onClose }) {
  const [results, setResults] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get(`/admin/elections/${election.election_id}/results`).then((r) => setResults(r.data)).catch(() => {});
    api.get(`/admin/elections/${election.election_id}/audit`).then((r) => setAudit(r.data.audit || [])).catch(() => {});
  }, [election.election_id]);

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-pearl rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-saffron">Live results · admin view</div>
            <div className="font-display text-lg text-navy">{election.title}</div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {results?.options && results.options.length > 0 ? (
            <div className="space-y-2">
              {results.options.map((o, i) => (
                <div key={o.option_id}>
                  <div className="flex justify-between text-sm">
                    <span className={i === 0 && o.votes > 0 ? "font-medium text-navy" : "text-navy/85"}>{o.label}</span>
                    <span className="font-mono text-navy/65">{o.votes} · {o.percent}%</span>
                  </div>
                  <div className="mt-1 h-2 bg-pearl-dark/30 rounded-full overflow-hidden">
                    <div className={`h-full ${i === 0 && o.votes > 0 ? "bg-saffron" : "bg-navy/60"}`} style={{ width: `${Math.max(o.percent, 0)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-center text-slate-500 py-6">No votes cast yet.</p>}

          {audit.length > 0 && (
            <div className="mt-6 border-t border-navy/10 pt-4">
              <div className="text-xs uppercase tracking-widest text-navy/55 flex items-center gap-1">
                <Lock size={11} /> Audit log · {audit.length} entries
              </div>
              <p className="text-[10px] text-navy/50 mt-1">Each entry records the anonymised voter token and timestamp only. No identities are stored.</p>
              <div className="mt-2 max-h-48 overflow-y-auto text-[11px] font-mono space-y-1">
                {audit.slice(0, 50).map((a, i) => (
                  <div key={i} className="text-navy/65">
                    <span className="text-slate-400">{new Date(a.at).toLocaleString("en-GB")}</span> · {a.event} · <span className="text-saffron">{a.voter_token.slice(0, 12)}…</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
